import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Sparkles, Send, Bot, User, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const suggestedQueries = [
  "Quali clienti dovrei contattare questa settimana?",
  "Analizza il mio portafoglio polizze",
  "Suggerimenti per aumentare le vendite",
  "Come gestire i sinistri aperti?",
  "Strategie di cross-selling per i miei clienti"
];

const AIAssistant = () => {
  const [query, setQuery] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;

    const userMessage = query;
    setQuery('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const response = await axios.post(`${API}/ai/insights`, {
        query: userMessage,
        context: ''
      });
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: response.data.insight,
        timestamp: response.data.generated_at
      }]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante la generazione dell\'insight');
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Mi dispiace, si è verificato un errore. Riprova più tardi.',
        error: true
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setQuery(suggestion);
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="ai-assistant-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
          AI Assistant
        </h1>
        <p className="text-slate-600 mt-1">Il tuo assistente intelligente per il settore assicurativo</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Chat Area */}
        <Card className="border-slate-200 lg:col-span-2">
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              Chat con Gemini AI
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {/* Messages */}
            <div className="h-[400px] overflow-y-auto p-4 space-y-4" data-testid="chat-messages">
              {messages.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-slate-500">
                  <Sparkles className="w-12 h-12 text-slate-300 mb-4" />
                  <p className="text-lg font-medium">Come posso aiutarti oggi?</p>
                  <p className="text-sm">Chiedi consigli sul tuo portafoglio assicurativo</p>
                </div>
              ) : (
                messages.map((message, index) => (
                  <div
                    key={index}
                    className={`flex gap-3 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    {message.role === 'assistant' && (
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center flex-shrink-0">
                        <Bot className="w-4 h-4 text-white" />
                      </div>
                    )}
                    <div
                      className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                        message.role === 'user'
                          ? 'bg-slate-900 text-white'
                          : message.error
                          ? 'bg-red-50 text-red-700 border border-red-200'
                          : 'bg-slate-100 text-slate-900'
                      }`}
                    >
                      <p className="whitespace-pre-wrap">{message.content}</p>
                    </div>
                    {message.role === 'user' && (
                      <div className="w-8 h-8 rounded-lg bg-slate-900 flex items-center justify-center flex-shrink-0">
                        <User className="w-4 h-4 text-white" />
                      </div>
                    )}
                  </div>
                ))
              )}
              {loading && (
                <div className="flex gap-3">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center flex-shrink-0">
                    <Bot className="w-4 h-4 text-white" />
                  </div>
                  <div className="bg-slate-100 rounded-2xl px-4 py-3">
                    <Loader2 className="w-5 h-5 animate-spin text-slate-500" />
                  </div>
                </div>
              )}
            </div>

            {/* Input */}
            <div className="border-t border-slate-100 p-4">
              <form onSubmit={handleSubmit} className="flex gap-3">
                <Textarea
                  placeholder="Scrivi la tua domanda..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  rows={2}
                  className="resize-none"
                  data-testid="ai-query-input"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSubmit(e);
                    }
                  }}
                />
                <Button
                  type="submit"
                  disabled={loading || !query.trim()}
                  className="bg-slate-900 hover:bg-slate-800 text-white px-6"
                  data-testid="ai-submit-btn"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </form>
            </div>
          </CardContent>
        </Card>

        {/* Suggestions */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-slate-900">
              Domande Suggerite
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {suggestedQueries.map((suggestion, index) => (
              <button
                key={index}
                onClick={() => handleSuggestionClick(suggestion)}
                className="w-full text-left p-3 rounded-lg bg-slate-50 hover:bg-slate-100 text-slate-700 text-sm transition-colors"
                data-testid={`suggestion-${index}`}
              >
                {suggestion}
              </button>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* Info */}
      <Card className="border-slate-200 bg-gradient-to-r from-blue-50 to-sky-50">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900 mb-1">Powered by Gemini 3 Flash</h3>
              <p className="text-sm text-slate-600">
                L'assistente AI analizza i tuoi dati per fornirti suggerimenti personalizzati sulla gestione del portafoglio,
                strategie di vendita e consigli per migliorare il servizio ai tuoi clienti.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AIAssistant;
